<script setup>
import { computed } from 'vue'
import { usePlanningStore } from '@/stores/planning.store'
import { usePortalStore } from '@/stores/portal.store'
import { useChips } from '@/composables/chips'
import { stringToColor, textColor } from '@/lib/utils'
import ListItem from '@/components/shared/ListItem.vue'

const props = defineProps({
  service: {
    type: Object,
    required: true,
  },
  // Names of the listing items (rate cards) associated with this service for the
  // selected listing; rendered as chips after the caption chips (default mode).
  listingItemNames: {
    type: Array,
    default: () => [],
  },
  // When true, show the listing item name(s) first as the main label and the real
  // service name greyed out right after (the listing chips are then hidden).
  listingFirst: {
    type: Boolean,
    default: false,
  },
})

const planningStore = usePlanningStore()
const portalStore = usePortalStore()
const { needQrScanChip, isVehicleChip, isSerializedChip, getServiceStructureTypeChip, archivedChip, rentedChip } = useChips()

const { STAFF, GROUP } = portalStore.serviceTypeEnum

const avatarIconMap = {
  [STAFF]: 'mdi-account',
  [GROUP]: 'mdi-select-group',
}

const getAvatarIcon = (s) => avatarIconMap[s.type] || 'mdi-camera-outline'

const listingText = computed(() => props.listingItemNames.join(', '))
const useListingFirst = computed(() => props.listingFirst && !!listingText.value)

// Main label / greyed suffix — swapped when listing-first mode is active
const mainLabel = computed(() =>
  useListingFirst.value ? listingText.value : planningStore.getServiceName(props.service),
)
const suffixLabel = computed(() =>
  useListingFirst.value ? planningStore.getServiceName(props.service) : undefined,
)

const getListingItemChips = () =>
  props.listingItemNames.map((name, i) => ({
    id: `li-${i}-${name}`,
    name,
    color: stringToColor(name),
    textColor: textColor(stringToColor(name)),
  }))

const getCaptionChips = (s) => [
  ...(s.type === STAFF ? [] : getServiceStructureTypeChip(s.structure_type)),
  ...(s.is_serialized ? [isSerializedChip] : []),
  // In listing-first mode the listing is the label, so don't repeat it as chips
  ...(useListingFirst.value ? [] : getListingItemChips()),
]

const getRightCaptionChips = (s) => {
  if (s.condition_value == null) return []
  const color = planningStore.getConditionColor(s.condition_value)
  return [{ name: planningStore.getConditionLabel(s.condition_value), color, textColor: textColor(color) }]
}

const getRightChips = (s) => {
  const chips = []
  if (s.is_rented)     chips.push(rentedChip)
  if (s.archived)      chips.push(archivedChip)
  if (s.need_qr_scan)  chips.push(needQrScanChip)
  if (s.is_vehicle)    chips.push(isVehicleChip)
  return chips
}
</script>

<template>
  <list-item
    v-bind="$attrs"
    :avatar="service.avatar_file || service.avatar_url"
    :avatar-icon="getAvatarIcon(service)"
    :label="mainLabel"
    :label-suffix="suffixLabel"
    :caption-chips="getCaptionChips(service)"
    :right-caption-chips="getRightCaptionChips(service)"
    :right-chips="getRightChips(service)"
    :disabled="service.archived"
  >
    <template v-if="$slots.right" #right>
      <slot name="right" />
    </template>
  </list-item>
</template>
